// src/data/claimLadder.js
//
// THE LIFECYCLE LADDER AND WHO MAY MOVE A CLAIM ALONG IT (weekend plan, batch 4).
//
// Pure functions - no React, no Firestore - so the authority boundary is proved by
// scripts\test-claim-ladder.mjs rather than eyeballed in a browser. Same split as
// src\data\deskQueue.js and src\data\gcDesk.js.
//
// THIS MODULE IS THE CLIENT HALF OF A TWO-HALF ANSWER, AND THE OTHER HALF IS THE REAL ONE.
// Ruling 16 puts enforcement in `firestore.rules`, not the UI. What lives here decides what a page
// OFFERS; what lives in the rules decides what the database ACCEPTS. If they ever disagree, the
// rules win and the page is the defect. The two are written from the same list below, deliberately,
// and scripts\test-firestore-rules.mjs asserts the rules half against the emulator.
//
// -------------------------------------------------------------------------------------------
// THE RULINGS THIS ENCODES, NAMED SO A LATER READER CAN CHECK IT RATHER THAN TRUST IT
//
//   61.2  ONE claim, ONE editable copy. Whoever holds it is the only person who can type.
//   61.6  SUPERSEDED BY RULING 72, 2026-08-29. It had held that `submitted -> draft` is not a legal
//         move and that lcMove() is elevate or hold and nothing else. Bob amended it: the return
//         exists, versioned and on a visible clock. 61.6's text is NOT edited - see
//         data\YCP_RULINGS_2026-08-29.md. The machinery is src\data\claimReturn.js.
//   72    A claim MAY be returned to its author by an LC (below gc_review) or a GC. The move is
//         'return'; everything that makes it safe - the reason, the frozen version, the untouched
//         filing window, the officer's content-free queue entry - lives in claimReturn.js.
//   56    A draft belongs to its author alone: no review, forwarding, copying or editing by anyone
//         else while it is in draft.
//   66    The administrator is not an exception in substance, only in reach: he may READ a draft,
//         nothing else, and every such read is audited. (Ex-62.)
//   27c   POSSESSION IS DRAFT, TRANSFER IS FREEZE. When the matter moves it freezes, and the prior
//         holder retains READ, not edit.
// -------------------------------------------------------------------------------------------

// The ladder. `gc_review` is NEW in batch 4 and it is the hinge of the whole model: it is the last
// rung a Local Chairperson may reach and the first rung that belongs to the General Chairperson.
export const LADDER = [
  { key: 'draft',                label: 'Draft',         icon: '📝', office: 'member' },
  { key: 'submitted',            label: 'Submitted',     icon: '📨', office: 'lc' },
  { key: 'intake',               label: 'Intake',        icon: '📋', office: 'lc' },
  { key: 'investigation',        label: 'Investigation', icon: '🔍', office: 'lc' },
  { key: 'documentation_review', label: 'Doc Review',    icon: '📄', office: 'lc' },
  { key: 'gc_review',            label: 'GC Review',     icon: '⬆️', office: 'gc' },
  { key: 'valuation',            label: 'Valuation',     icon: '💰', office: 'gc' },
  { key: 'negotiation',          label: 'Negotiation',   icon: '🤝', office: 'gc' },
  { key: 'settlement',           label: 'Settlement',    icon: '✍️', office: 'gc' },
  { key: 'closure',              label: 'Closure',       icon: '✅', office: 'gc' },
];

export const STAGE_ORDER = LADDER.map(s => s.key);

// The rung at which a claim leaves the Local Chairperson's desk. He may ADVANCE a claim TO it -
// that is the elevation - and he may not move it past.
export const ELEVATION_STAGE = 'gc_review';

// Stages an LC may set. Ends at gc_review inclusive. `draft` is deliberately absent (61.6).
export const LC_SETTABLE = ['submitted', 'intake', 'investigation', 'documentation_review', 'gc_review'];

// Off-ladder states. A claim in one of these is not "at a stage" and the stage bar says so rather
// than rendering a position it does not have.
export const OFF_LADDER = ['suspended', 'archived'];

export function stageIndex(status) {
  return STAGE_ORDER.indexOf(String(status || ''));
}

export function isDraft(claim) {
  return String((claim && claim.status) || '') === 'draft';
}

// WHO MAY SEE A CLAIM AT ALL.
//
// The draft carve-out is the whole point and it is the thing that changed today: before batch 4 a
// Local Chairperson in scope, and a General Chairperson in committee, could read a member's DRAFT.
// Ruling 56 says a draft belongs to its author alone. Ruling 66 gives the administrator read and
// nothing else, because the literal reading would make an orphaned draft - an author who leaves,
// dies, or loses his account - unreachable by anyone, forever.
//
// `opts.adminReadIsAudited` exists to make the obligation visible at the call site rather than
// implied: ruling 66 requires that EVERY administrative read of a draft is written to the audit
// collection. A security rule cannot compel a side effect, so this is a client and function
// obligation, and verifier check AUD-1 is what makes it real.
export function canRead(claim, user) {
  if (!claim || !user) return false;
  const owner = !!user.uid && user.uid === claim.createdBy;
  if (owner) return true;
  if (user.isAdmin) return true;                       // ruling 66: read, and the read is audited
  if (isDraft(claim)) return false;                    // ruling 56: nobody else, whatever the title
  if (user.role === 'local_chair') {
    return Array.isArray(user.scopes) && user.scopes.indexOf(claim.scopeKey) !== -1;
  }
  if (user.role === 'general_chair') {
    return !!user.committee && user.committee === claim.committee;
  }
  return false;
}

// WHO MAY EDIT THE SUBSTANCE OF A CLAIM AS IT STANDS.
//
// 27c in one function: whoever holds it. A claim past gc_review has left the Local Chairperson's
// hands and he keeps read, not edit - which is what "transfer is freeze" means mechanically.
// The administrator is NOT an editor of a draft (66): read only.
export function canEdit(claim, user) {
  if (!claim || !user) return false;
  const owner = !!user.uid && user.uid === claim.createdBy;
  if (isDraft(claim)) return owner;                    // 56 + 66: the author alone, admin excluded
  if (user.isAdmin) return true;
  if (user.role === 'general_chair') {
    return !!user.committee && user.committee === claim.committee;
  }
  if (user.role === 'local_chair') {
    const inScope = Array.isArray(user.scopes) && user.scopes.indexOf(claim.scopeKey) !== -1;
    if (!inScope) return false;
    const i = stageIndex(claim.status);
    // At or past gc_review the matter has moved up. He may still read it; he may not type in it.
    return i >= 0 && i < stageIndex(ELEVATION_STAGE);
  }
  return false;                                        // a member does not edit his own filed claim
}

// WHO MAY ATTACH A DOCUMENT TO A CLAIM (forensic audit 2026-10-16). An officer: exactly where he may edit it (canEdit -
// lcEdit/gcEdit/adminEdit), because the document is recorded on the claim and a refused record leaves an orphan file in
// Storage. Anyone else: the claim's CREATOR once it is filed (firestore.rules ownerAttachesDocuments()). The claimant of a
// claim an officer filed for her is NOT the creator, so she cannot - her Local Chairperson saves her signed copy.
// ClaimDocuments offers upload by this, and ClaimDetail words the signed-copy instruction by it.
export function mayAttachDocuments(claim, user) {
  if (!claim || !user) return false;
  const officer = !!user.isAdmin || user.role === 'general_chair' || user.role === 'local_chair';
  if (officer) return canEdit(claim, user);
  return !isDraft(claim) && !!user.uid && claim.createdBy === user.uid;
}

// THE MOVES A LOCAL CHAIRPERSON HAS. Three since ruling 72 amended 61.6 on 2026-08-29.
//
// Returns the next status for `move`, or null when the move is not his to make.
//
// 'return' YIELDS 'draft' AND THAT IS DELIBERATE, where the previous version of this function
// deliberately COULD NOT express it. The guard has moved rather than disappeared: this function
// answers "is that a legal transition", and src\data\claimReturn.js answers "may this person do it,
// with what reason, and what does the filing window say about it right now". A return with no reason
// is refused there, not here.
export function lcMove(claim, move) {
  // A DRAFT IS NOT HIS TO MOVE, INCLUDING TO HOLD IT WHERE IT IS. Caught by
  // scripts\test-claim-ladder.mjs on its first run: without this line `hold` returned 'draft',
  // because draft is a rung on the ladder and hold returns the current rung. Harmless-looking, and
  // wrong - ruling 56 means a Local Chairperson cannot so much as SEE a draft, so a function that
  // hands him a legal move on one is a contradiction waiting to be called by a later caller who
  // trusts it. He has no moves on a draft at all.
  if (isDraft(claim)) return null;
  const i = stageIndex(claim && claim.status);
  if (i < 0) return null;                                        // off-ladder or unknown
  if (move === 'hold') return STAGE_ORDER[i];                    // stays exactly where it is
  const cap = stageIndex(ELEVATION_STAGE);
  // RULING 72. A return sends the claim to draft, and only from a rung he still holds: at or past
  // gc_review the matter has moved up (27c) and he can send it neither onward nor back.
  if (move === 'return') return i < cap ? 'draft' : null;
  if (move !== 'elevate') return null;                           // elevate, hold, return - no fourth
  if (i >= cap) return null;                                     // already at or past his ceiling
  return STAGE_ORDER[i + 1];
}

// THE NEXT STAGE THIS USER MAY ADVANCE THIS CLAIM TO, or null.
//
// One function so the page cannot offer a button the rules will refuse. A member never advances -
// his one move, draft -> submitted, is a separate act on his own document and is not an advance
// along the officer ladder.
export function nextStageFor(claim, user) {
  if (!claim || !user) return null;
  if (OFF_LADDER.indexOf(String(claim.status || '')) !== -1) return null;
  const i = stageIndex(claim.status);
  if (i < 0 || i >= STAGE_ORDER.length - 1) return null;
  if (isDraft(claim)) return null;                     // nobody advances a draft off the ladder
  if (!canEdit(claim, user)) return null;
  if (user.isAdmin || user.role === 'general_chair') return STAGE_ORDER[i + 1];
  if (user.role === 'local_chair') return lcMove(claim, 'elevate');
  return null;
}

// Why the advance control is absent, in words an officer can act on. An unavailable button with no
// explanation is the defect this exists to prevent - PD-5, and the same reason LCDesk renders its
// unbuilt sections rather than omitting them.
export function advanceBlockedReason(claim, user) {
  if (!claim || !user) return 'No claim loaded.';
  if (OFF_LADDER.indexOf(String(claim.status || '')) !== -1) {
    return 'This claim is ' + claim.status + ' and is off the lifecycle ladder.';
  }
  if (isDraft(claim)) {
    return 'This is a draft. It belongs to its author alone until he submits it; '
         + 'no office advances it for him.';
  }
  const i = stageIndex(claim.status);
  if (i < 0) return 'This claim carries a status that is not on the ladder: ' + String(claim.status) + '.';
  if (i >= STAGE_ORDER.length - 1) return 'This claim is at the last stage on the ladder.';
  if (user.role === 'local_chair' && i >= stageIndex(ELEVATION_STAGE)) {
    return 'You elevated this claim to GC Review. Under ruling 27c the matter has moved and you keep '
         + 'read, not edit - you can send it neither onward nor back from here. Raise it with your '
         + 'General Chairperson.';
  }
  if (!canEdit(claim, user)) return 'This claim is outside your authority.';
  return '';
}
